/**
 * AWDD Theme — Main JavaScript
 * Uses GSAP + ScrollTrigger for animations
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

document.addEventListener('DOMContentLoaded', function () {

    // ===== Mobile menu toggle =====
    const mobileMenuBtn = document.getElementById('mobileMenuBtn');
    const mobileMenu = document.getElementById('mobileMenu');

    // ===== Nav hide/show on scroll direction =====
    // Skip nav-hidden on mobile (would also hide the hamburger — only menu entry point)
    // and while the mobile menu is open (would slide the open menu offscreen).
    const nav = document.getElementById('mainNav');
    if (nav) {
        let lastY = 0;
        window.addEventListener('scroll', () => {
            const y = window.scrollY;
            nav.classList.toggle('scrolled', y > 40);
            const isMobile = window.innerWidth < 768;
            const menuOpen = mobileMenu && !mobileMenu.classList.contains('hidden');
            nav.classList.toggle('nav-hidden', !isMobile && !menuOpen && y > lastY && y > 80);
            lastY = y;
        }, { passive: true });
    }

    if (mobileMenuBtn && mobileMenu) {
        mobileMenuBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const isHidden = mobileMenu.classList.toggle('hidden');
            mobileMenuBtn.setAttribute('aria-expanded', String(!isHidden));
        });

        // Close mobile menu when a link is clicked
        mobileMenu.querySelectorAll('a').forEach(a => {
            a.addEventListener('click', () => {
                mobileMenu.classList.add('hidden');
                mobileMenuBtn.setAttribute('aria-expanded', 'false');
            });
        });

        // Close menu on outside tap
        document.addEventListener('click', (e) => {
            if (!mobileMenu.classList.contains('hidden') && !mobileMenu.contains(e.target) && !mobileMenuBtn.contains(e.target)) {
                mobileMenu.classList.add('hidden');
                mobileMenuBtn.setAttribute('aria-expanded', 'false');
            }
        });
    }

    // ===== Hero entrance animation =====
    const heroElements = document.querySelectorAll('.hero-h');
    if (heroElements.length) {
        const htl = gsap.timeline({ delay: 0.1 });
        htl.from('.hero-h', { opacity: 0, y: 40, duration: 0.9, ease: 'power2.out', stagger: 0.08 })
           .from('.hero-div', { opacity: 0, y: 16, duration: 0.6, ease: 'power2.out' }, '-=0.5')
           .from('.hero-bottom', { opacity: 0, y: 20, duration: 0.6, ease: 'power2.out' }, '-=0.4')
           .from('.hero-strip', { opacity: 0, y: 8, duration: 0.5, ease: 'power2.out' }, '-=0.3');
    }

    // ===== Scroll reveals =====
    document.querySelectorAll('.reveal').forEach(el => {
        const delayMatch = el.className.match(/reveal-delay-(\d+)/);
        const delay = delayMatch ? parseInt(delayMatch[1]) * 0.15 : 0;

        gsap.fromTo(el,
            { autoAlpha: 0, y: 28 },
            {
                autoAlpha: 1, y: 0, duration: 0.8, ease: 'power2.out',
                delay,
                scrollTrigger: { trigger: el, start: 'top 90%', toggleActions: 'play none none none' }
            }
        );
    });

    // Recalculate ScrollTrigger positions after webfonts and images settle —
    // otherwise reveals below the fold can stay hidden when initial layout
    // shifts (e.g. font swap, GF AJAX iframe expanding, lazy-loaded images).
    window.addEventListener('load', () => ScrollTrigger.refresh());
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => ScrollTrigger.refresh());
    }
    // Safety net: if any .reveal element is still hidden 2s after load (because
    // ScrollTrigger never fired for it), force-show it. Matches the "always
    // visible" intent — the fade is a nicety, not a gate.
    window.addEventListener('load', () => {
        setTimeout(() => {
            document.querySelectorAll('.reveal').forEach(el => {
                const cs = getComputedStyle(el);
                if (cs.visibility === 'hidden') {
                    gsap.set(el, { autoAlpha: 1, y: 0 });
                }
            });
        }, 2000);
    });

    // ===== Counter animation =====
    document.querySelectorAll('[data-counter]').forEach(el => {
        const target = parseFloat(el.dataset.counter);
        const suffix = el.dataset.suffix || '';
        const isDecimal = el.dataset.decimal === 'true';
        ScrollTrigger.create({
            trigger: el, start: 'top 80%', once: true,
            onEnter: () => {
                gsap.to({ val: 0 }, {
                    val: target, duration: 1.8, ease: 'power2.out',
                    onUpdate: function () {
                        const v = this.targets()[0].val;
                        el.textContent = (isDecimal ? v.toFixed(1) : Math.floor(v)) + suffix;
                    }
                });
            }
        });
    });

    // ===== Metric bars (Results Comparison) =====
    // Each .metric-bar starts at width:0 (inline style); when in viewport, swap to data-width.
    // CSS transition on .metric-bar handles the fill animation.
    document.querySelectorAll('.metric-bar').forEach(bar => {
        const target = bar.dataset.width;
        if (!target) return;
        ScrollTrigger.create({
            trigger: bar, start: 'top 90%', once: true,
            onEnter: () => { bar.style.width = target; }
        });
    });

    // ===== Parallax — background images =====
    document.querySelectorAll('[data-parallax]').forEach(img => {
        const section = img.closest('section') || img.parentElement;
        gsap.to(img, {
            yPercent: -15,
            ease: 'none',
            scrollTrigger: {
                trigger: section,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true
            }
        });
    });

    // ===== Service Tabs =====
    const serviceTabs = document.querySelectorAll('.service-tab');
    const servicePanels = document.querySelectorAll('.service-panel');

    if (serviceTabs.length) {
        serviceTabs.forEach(tab => {
            tab.addEventListener('click', () => {
                const targetId = tab.getAttribute('aria-controls');

                // Update tabs
                serviceTabs.forEach(t => {
                    t.setAttribute('aria-selected', 'false');
                    t.classList.remove('active');
                });
                tab.setAttribute('aria-selected', 'true');
                tab.classList.add('active');

                // Update panels
                servicePanels.forEach(p => p.classList.remove('active'));
                const target = document.getElementById(targetId);
                if (target) target.classList.add('active');
            });
        });
    }

    // ===== Digital OS Flow Tabs =====
    const dosDetails = document.getElementById('dos-details');
    const dosTabs = document.querySelectorAll('.dos-tab');

    if (dosTabs.length && dosDetails) {
        dosTabs.forEach(tab => {
            tab.addEventListener('click', () => {
                const tabNum = tab.dataset.dosTab;
                const panel = document.getElementById('dos-panel-' + tabNum);

                // Remove active from all tabs and panels
                document.querySelectorAll('.dos-panel').forEach(p => p.classList.remove('active'));
                dosTabs.forEach(t => t.classList.remove('active'));

                // Activate clicked tab + panel
                tab.classList.add('active');
                if (panel) panel.classList.add('active');

                // Show the details container
                dosDetails.classList.remove('hidden');
            });
        });
    }

    // ===== Quiz =====
    const progressBar = document.getElementById('quizProgress');
    if (progressBar) {
        let quizAnswers = [];
        let currentQ = 0;
        const progressWrap = progressBar.parentElement;
        const announcer = document.getElementById('quizAnnouncer');
        const stepDots = [
            document.querySelector('.quiz-dot-1'),
            document.querySelector('.quiz-dot-2'),
            document.querySelector('.quiz-dot-3'),
        ];

        function updateStepDots(activeIndex) {
            stepDots.forEach((dot, i) => {
                if (!dot) return;
                if (i < activeIndex) {
                    dot.className = dot.className.replace(/border-white\/15|text-white\/30|bg-brand-red|border-brand-red/g, '').trim();
                    dot.classList.add('bg-brand-red/20', 'border-brand-red/40', 'text-brand-red');
                    dot.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><polyline points="1.5 5 4 7.5 8.5 2.5"/></svg>';
                } else if (i === activeIndex) {
                    dot.className = dot.className.replace(/bg-brand-red\/20|border-brand-red\/40|text-brand-red|border-white\/15|text-white\/30/g, '').trim();
                    dot.classList.add('bg-brand-red', 'border-brand-red', 'text-white');
                    dot.textContent = i + 1;
                } else {
                    dot.className = dot.className.replace(/bg-brand-red\/20|border-brand-red\/40|bg-brand-red|border-brand-red|text-brand-red|text-white(?!\/)/g, '').trim();
                    dot.classList.add('border-white/15', 'text-white/30');
                    dot.textContent = i + 1;
                }
            });
        }

        document.querySelectorAll('.quiz-answer').forEach(btn => {
            btn.addEventListener('click', function () {
                quizAnswers.push(parseInt(this.dataset.value));
                currentQ++;
                document.querySelectorAll('.quiz-step').forEach(s => s.classList.remove('active'));

                var totalQ = (window.awddQuizResults && window.awddQuizResults.totalQuestions) ? window.awddQuizResults.totalQuestions : 3;
                if (currentQ < totalQ) {
                    const pct = Math.round((currentQ + 1) / totalQ * 100);
                    progressBar.style.width = pct + '%';
                    progressWrap.setAttribute('aria-valuenow', pct);
                    updateStepDots(currentQ);
                    document.querySelector('.quiz-step[data-question="' + currentQ + '"]').classList.add('active');
                    if (announcer) announcer.textContent = 'Question ' + (currentQ + 1) + ' of ' + totalQ;
                } else {
                    const score = quizAnswers.reduce((a, b) => a + b, 0);
                    const maxScore = totalQ * 3;
                    const pct = Math.round((score / maxScore) * 100);
                    progressBar.style.width = '100%';
                    progressWrap.setAttribute('aria-valuenow', 100);
                    updateStepDots(3);
                    const scoreEl = document.getElementById('quizScore');
                    if (scoreEl) scoreEl.textContent = pct + '%';
                    const arc = document.getElementById('quizArc');
                    if (arc) {
                        const circumference = 314.16;
                        arc.style.strokeDashoffset = circumference * (1 - pct / 100);
                    }
                    var headline, bodyText;
                    var r = window.awddQuizResults;
                    if (r) {
                        headline = pct > 50 ? r.high.headline : r.low.headline;
                        bodyText = pct > 50 ? r.high.body : r.low.body;
                    } else {
                        headline = pct > 50 ? "There's significant room to improve" : "You're in decent shape — but there's upside";
                        bodyText = pct > 50
                            ? "Your digital presence has real gaps. A Roadmap Workshop will give you a clear, prioritised plan to fix what matters most."
                            : "You've got foundations in place, but there are opportunities to connect the dots and get more from what you're already spending.";
                    }
                    const headlineEl = document.getElementById('quizHeadline');
                    const bodyEl = document.getElementById('quizBody');
                    if (headlineEl) headlineEl.textContent = headline;
                    if (bodyEl) bodyEl.textContent = bodyText;
                    const resultStep = document.querySelector('.quiz-step[data-question="result"]');
                    if (resultStep) resultStep.classList.add('active');
                    if (announcer) announcer.textContent = 'Quiz complete. ' + headline;
                }
            });
        });
    }

    // ===== Category filter (article-grid-filterable + project-grid) =====
    function initCategoryFilter(gridId, btnClass) {
        const grid = document.getElementById(gridId);
        if (!grid) return;
        const btns = document.querySelectorAll('.' + btnClass);
        btns.forEach(btn => {
            btn.addEventListener('click', () => {
                const filter = btn.dataset.filter;
                btns.forEach(b => b.classList.remove('active'));
                btn.classList.add('active');
                grid.querySelectorAll('[data-category]').forEach(card => {
                    const cats = card.dataset.category || '';
                    card.classList.toggle('filter-hidden',
                        filter !== 'all' && !cats.split(' ').includes(filter)
                    );
                });
            });
        });
    }
    initCategoryFilter('articlesGrid', 'filter-btn-dark');
    initCategoryFilter('projectGrid', 'filter-btn-light');

    // ===== Smooth scroll =====
    document.querySelectorAll('a[href^="#"]').forEach(a => {
        a.addEventListener('click', function (e) {
            const t = document.querySelector(this.getAttribute('href'));
            if (t) {
                e.preventDefault();
                t.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
    });

    /* ── Scroll to top button ── */
    const scrollTopBtn = document.getElementById('scrollTopBtn');
    if (scrollTopBtn) {
        window.addEventListener('scroll', () => {
            scrollTopBtn.classList.toggle('visible', window.scrollY > 400);
        }, { passive: true });
        scrollTopBtn.addEventListener('click', () => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }

    // ===== Single Post: reading progress bar =====
    const readingProgress = document.getElementById('reading-progress');
    if (readingProgress) {
        const updateProgress = () => {
            const docHeight = document.documentElement.scrollHeight - window.innerHeight;
            const progress = docHeight > 0 ? Math.min((window.scrollY / docHeight) * 100, 100) : 0;
            readingProgress.style.width = progress + '%';
        };
        window.addEventListener('scroll', updateProgress, { passive: true });
        updateProgress();
    }

    // ===== Single Post: sidebar TOC scrollspy =====
    const tocLinks = document.querySelectorAll('#toc .toc-link');
    if (tocLinks.length) {
        const tocSections = Array.from(tocLinks)
            .map(l => document.getElementById(l.dataset.section))
            .filter(Boolean);
        const updateTOC = () => {
            let current = '';
            tocSections.forEach(s => {
                if (s.getBoundingClientRect().top <= 150) current = s.id;
            });
            tocLinks.forEach(l => l.classList.toggle('active', l.dataset.section === current));
        };
        window.addEventListener('scroll', updateTOC, { passive: true });
        updateTOC();
    }

    // ===== Single Post: FAQ accordion =====
    document.querySelectorAll('.faq-toggle').forEach(btn => {
        btn.addEventListener('click', () => {
            const item = btn.closest('.faq-item');
            if (!item) return;
            const answer = item.querySelector('.faq-answer');
            const chevron = item.querySelector('.faq-chevron');
            const isOpen = answer && answer.classList.contains('open');

            document.querySelectorAll('.faq-answer.open').forEach(a => a.classList.remove('open'));
            document.querySelectorAll('.faq-chevron.open').forEach(c => c.classList.remove('open'));
            document.querySelectorAll('.faq-toggle[aria-expanded="true"]').forEach(b => b.setAttribute('aria-expanded', 'false'));

            if (!isOpen) {
                if (answer) answer.classList.add('open');
                if (chevron) chevron.classList.add('open');
                btn.setAttribute('aria-expanded', 'true');
            }
        });
    });

});

// ===== Single Post: share helpers (must be window-global for inline onclick) =====
function awddOpenShare(url) {
    const w = 600, h = 500;
    const left = (window.screen.width  - w) / 2;
    const top  = (window.screen.height - h) / 2;
    const win = window.open(url, '_blank', `width=${w},height=${h},left=${left},top=${top},noopener`);
    if (!win) {
        // Popup blocked — fall back to navigating in a new tab.
        window.open(url, '_blank', 'noopener');
    }
}

window.shareLinkedIn = function () {
    awddOpenShare('https://www.linkedin.com/sharing/share-offsite/?url=' + encodeURIComponent(window.location.href));
};

window.shareX = function () {
    const heading = document.querySelector('h1');
    const text = heading ? heading.textContent.trim() : document.title;
    awddOpenShare('https://twitter.com/intent/tweet?text=' + encodeURIComponent(text) + '&url=' + encodeURIComponent(window.location.href));
};

function awddCopyFlash(success) {
    const btn = document.getElementById('copy-btn');
    if (!btn) return;
    const original = btn.dataset.originalHtml || btn.innerHTML;
    btn.dataset.originalHtml = original;
    btn.innerHTML = success
        ? '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8.5l3 3 7-7" stroke="#10b981" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>'
        : '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M12 4L4 12M4 4l8 8" stroke="#de4d44" stroke-width="1.75" stroke-linecap="round"/></svg>';
    setTimeout(() => { btn.innerHTML = original; }, 1800);
}

window.copyLink = function () {
    const url = window.location.href;
    // Modern path — works on HTTPS and localhost.
    if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url)
            .then(() => awddCopyFlash(true))
            .catch(() => awddCopyFallback(url));
        return;
    }
    awddCopyFallback(url);
};

function awddCopyFallback(text) {
    // execCommand fallback for non-secure contexts (e.g. *.test dev domains over http://).
    let ok = false;
    try {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.top = '-1000px';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, ta.value.length);
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
    } catch (e) {
        ok = false;
    }
    awddCopyFlash(ok);
}
